import React, {ReactElement} from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Typography
} from '@customink/pigment-react';
import IconButton from '@mui/material/IconButton';
import {selectAdmin} from '../../slices/userSlice';
import {useAppSelector, useStoreSlug} from '../../hooks';
import {StoreProduct} from '../../models/storeProduct';
import './index.scss';
import DeleteIcon from '../../assets/icons/trashcan.svg';

type DeletionDialogProps = {
  product: StoreProduct;
  disableProduct: (product: StoreProduct) => void;
  deleteProductsEnabled: boolean;
  setAddedProductsQty: (quantity: string | null) => void;
};

type DeleteProductEvent = {
  event:
    | 'remove_products'
    | 'remove_products_disabled'
    | 'remove_products_cancel'
    | 'remove_products_confirm';
  status: 'internal' | 'external';
  id: string;
};

const publishDeleteProductEvent = (event: DeleteProductEvent) => {
  if (window.dataLayer && Array.isArray(window.dataLayer)) {
    window.dataLayer.push(event);
  }
};

const DeletionDialog: React.FC<DeletionDialogProps> = ({
  product,
  disableProduct,
  deleteProductsEnabled,
  setAddedProductsQty
}: DeletionDialogProps): ReactElement => {
  const [open, setOpen] = React.useState(false);
  const isAdmin = useAppSelector(selectAdmin);
  const storeSlug = useStoreSlug();

  const deleteDisabled = deleteProductsEnabled && product.deletable === false;

  const handleClickOpen = () => {
    if (deleteDisabled) {
      publishDeleteProductEvent({
        event: 'remove_products_disabled',
        status: isAdmin ? 'internal' : 'external',
        id: storeSlug
      });
      return;
    }

    publishDeleteProductEvent({
      event: 'remove_products',
      status: isAdmin ? 'internal' : 'external',
      id: storeSlug
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    publishDeleteProductEvent({
      event: 'remove_products_cancel',
      status: isAdmin ? 'internal' : 'external',
      id: storeSlug
    });
  };

  const handleDisableProduct = () => {
    publishDeleteProductEvent({
      event: 'remove_products_confirm',
      status: isAdmin ? 'internal' : 'external',
      id: storeSlug
    });

    disableProduct(product);
    setAddedProductsQty(null);
  };

  return (
    <div>
      <IconButton
        aria-label="Delete Product"
        className={`deleteIconBtn ${
          deleteDisabled ? 'deleteIconBtnDisabled' : ''
        }`}
        onClick={handleClickOpen}>
        <img src={DeleteIcon} alt="Delete Product" />
      </IconButton>
      <Dialog
        title="Are you sure you want to delete this product?"
        titleSx={{paddingTop: '0.5rem'}}
        onClose={handleClose}
        open={open}>
        <DialogContent pt={2}>
          <Typography variant="bodyLong1">
            Once you delete a product, it can&rsquo;t be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            className="cancel"
            size="large"
            variant="secondary"
            onClick={handleClose}>
            Cancel
          </Button>
          <Button
            className="delete"
            size="large"
            variant="primary"
            onClick={handleDisableProduct}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DeletionDialog;
